import type { FeatureResultState } from "@nexus/contracts";
import type { IconName } from "@nexus/ui";

/** Tone tokens used for per-feature result rendering (color always paired with text/icon — KTD15). */
export type ResultTone = "success" | "warning" | "error" | "muted";

/** Background fill class per tone (shared across the meter, heatmap, blockers, and rail). */
export const TONE_BG: Record<ResultTone, string> = {
  success: "bg-success",
  warning: "bg-warning",
  error: "bg-error",
  muted: "bg-muted-foreground/40",
};

/** Subtle row-tint fill per tone — colours a result row without competing with the swatches. */
export const TONE_ROW_BG: Record<ResultTone, string> = {
  success: "bg-success-subtle/50",
  warning: "bg-warning-subtle/60",
  error: "bg-error-subtle/50",
  muted: "",
};

/** Foreground text class per tone (always pair color with a label/icon). */
export const TONE_TEXT: Record<ResultTone, string> = {
  success: "text-success",
  warning: "text-warning",
  error: "text-error",
  muted: "text-muted-foreground",
};

/** Render order so stacked meters read as a clean earned → partial → lost gradient. */
export const TONE_ORDER: ResultTone[] = [
  "success",
  "warning",
  "error",
  "muted",
];

/** Resolve a feature result state to its semantic tone. */
export function stateTone(state: FeatureResultState): ResultTone {
  return RESULT_STATE_META[state].tone;
}

interface ResultStateMeta {
  /** i18n key for the result label; null renders the muted "—" dash. */
  labelKey: string | null;
  icon: IconName;
  tone: ResultTone;
  /** i18n key for the notes-column explanation of the three excluded states (KTD8); null when the result speaks for itself. */
  noteKey: string | null;
  /** Whether this state contributes to the score (the three excluded states do not). */
  excluded: boolean;
}

/**
 * Display vocabulary for the six `FeatureResultState` cases (KTD8). PARTIAL shows its
 * fractional score distinctly; the three excluded states render a muted "—" + note.
 * Text is referenced as i18n keys and resolved at the call site.
 */
export const RESULT_STATE_META: Record<FeatureResultState, ResultStateMeta> = {
  PASS: {
    labelKey: "preflight.resultState.PASS",
    icon: "check",
    tone: "success",
    noteKey: "preflight.resultState.note.pass",
    excluded: false,
  },
  FAIL: {
    labelKey: "preflight.resultState.FAIL",
    icon: "x",
    tone: "error",
    noteKey: "preflight.resultState.note.fail",
    excluded: false,
  },
  PARTIAL: {
    labelKey: "preflight.resultState.PARTIAL",
    icon: "alert-circle",
    tone: "warning",
    noteKey: "preflight.resultState.note.partial",
    excluded: false,
  },
  NOT_WEIGHTED: {
    labelKey: null,
    icon: "info",
    tone: "muted",
    noteKey: "preflight.resultState.note.notWeighted",
    excluded: true,
  },
  NOT_APPLICABLE: {
    labelKey: null,
    icon: "info",
    tone: "muted",
    noteKey: "preflight.resultState.note.notApplicable",
    excluded: true,
  },
  NOT_ANALYZED: {
    labelKey: null,
    icon: "info",
    tone: "muted",
    noteKey: "preflight.resultState.note.notAnalyzed",
    excluded: true,
  },
} as const;
